import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  fetchOnboardingFlowById,
  saveOnboardingFlow,
  saveOnboardingPage,
  deleteOnboardingPage,
  updateOnboardingPagePositions,
} from "@/api/onboarding";
import AdminLayout from "@/components/layout/AdminLayout";
import OnboardingPreview from "@/components/onboarding/OnboardingPreview";
import OnboardingPageForm from "@/components/onboarding/OnboardingPageForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";
import type { OnboardingPage, LocalizedText } from "@/types/onboarding";

const LOCALES = [
  { code: "en", label: "English" },
  { code: "mz", label: "Mizo" },
] as const;

function lt(value: LocalizedText | string | null | undefined, locale: string): string {
  if (!value) return "";
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed[locale] || parsed["en"] || Object.values(parsed)[0] || "";
      }
    } catch {
      // not JSON, use as plain string
    }
    return value;
  }
  return value[locale] || value["en"] || Object.values(value)[0] || "";
}
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const flowSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z.string().min(1, "Slug is required"),
  description: z.string().optional(),
  isActive: z.boolean(),
  isSkippable: z.boolean(),
});

type FlowFormValues = z.infer<typeof flowSchema>;

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// Sortable page card
function SortablePageCard({
  page,
  isSelected,
  onClick,
  onEdit,
  onDelete,
}: {
  page: OnboardingPage;
  isSelected: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        isDragging ? "opacity-50" : ""
      } ${
        isSelected
          ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-400"
          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
      }`}
      onClick={onClick}
    >
      <button
        className="cursor-grab touch-none text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
            #{page.position + 1}
          </span>
          <span className="text-sm font-medium truncate dark:text-gray-200">{lt(page.title, "en")}</span>
          {!page.isActive && (
            <Badge variant="secondary" className="text-xs">
              <EyeOff className="w-3 h-3 mr-1" />
              Hidden
            </Badge>
          )}
        </div>
        {lt(page.tag, "en") && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{lt(page.tag, "en")}</span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-500 hover:text-red-700"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function OnboardingFlowForm() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [pageFormOpen, setPageFormOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<OnboardingPage | null>(null);
  const [deletingPageId, setDeletingPageId] = useState<number | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [previewLocale, setPreviewLocale] = useState("en");

  const form = useForm<FlowFormValues>({
    resolver: zodResolver(flowSchema),
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      isActive: true,
      isSkippable: true,
    },
  });

  const {
    data: flow,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["onboarding-flow", id],
    queryFn: () => fetchOnboardingFlowById(Number(id)),
    enabled: isEditMode,
  });

  useEffect(() => {
    if (flow) {
      form.reset({
        name: flow.name,
        slug: flow.slug,
        description: flow.description || "",
        isActive: flow.isActive,
        isSkippable: flow.isSkippable,
      });
      setSlugManuallyEdited(true);
      if (flow.pages.length > 0 && !selectedPageId) {
        setSelectedPageId(flow.pages[0].id);
      }
    }
  }, [flow, form]);

  // Auto-generate slug from name
  const watchedName = form.watch("name");
  useEffect(() => {
    if (!slugManuallyEdited && watchedName) {
      form.setValue("slug", generateSlug(watchedName));
    }
  }, [watchedName, slugManuallyEdited, form]);

  const flowMutation = useMutation({
    mutationFn: (values: FlowFormValues) =>
      saveOnboardingFlow(values, isEditMode ? Number(id) : undefined),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-flows"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-flow"] });
      toast({
        title: isEditMode ? "Flow updated" : "Flow created",
        description: isEditMode
          ? "The onboarding flow has been updated."
          : "The onboarding flow has been created. You can now add pages.",
      });
      if (!isEditMode) {
        navigate(`/admin/onboarding/${data.id}/edit`, { replace: true });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deletePageMutation = useMutation({
    mutationFn: deleteOnboardingPage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-flow"] });
      toast({ title: "Page deleted" });
      setDeletingPageId(null);
      if (selectedPageId === deletingPageId) {
        setSelectedPageId(null);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const positionMutation = useMutation({
    mutationFn: updateOnboardingPagePositions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-flow"] });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !flow?.pages) return;

    const oldIndex = flow.pages.findIndex((p) => p.id === active.id);
    const newIndex = flow.pages.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(flow.pages, oldIndex, newIndex);
    const updates = reordered.map((p, idx) => ({ id: p.id, position: idx }));

    // Optimistic update
    queryClient.setQueryData(["onboarding-flow", id], {
      ...flow,
      pages: reordered.map((p, idx) => ({ ...p, position: idx })),
    });

    positionMutation.mutate(updates);
  };

  const onSubmit = (values: FlowFormValues) => {
    flowMutation.mutate(values);
  };

  const selectedPage =
    flow?.pages.find((p) => p.id === selectedPageId) || null;

  if (isEditMode && isLoading) {
    return (
      <AdminLayout title="Loading...">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      </AdminLayout>
    );
  }

  if (isEditMode && isError) {
    return (
      <AdminLayout title="Error">
        <Card>
          <CardContent className="py-12 text-center text-red-500 dark:text-red-400">
            Failed to load onboarding flow.
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? "Edit Onboarding Flow" : "New Onboarding Flow"}>
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/onboarding")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Flows
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form + Pages */}
        <div className="lg:col-span-2 space-y-6">
          {/* Flow details card */}
          <Card>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader>
                <CardTitle>Flow Details</CardTitle>
                <CardDescription>
                  Configure the onboarding flow settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" {...form.register("name")} />
                    {form.formState.errors.name && (
                      <p className="text-sm text-red-500">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug *</Label>
                    <Input
                      id="slug"
                      {...form.register("slug")}
                      onChange={(e) => {
                        setSlugManuallyEdited(true);
                        form.setValue("slug", e.target.value);
                      }}
                    />
                    {form.formState.errors.slug && (
                      <p className="text-sm text-red-500">
                        {form.formState.errors.slug.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    rows={2}
                    {...form.register("description")}
                  />
                </div>
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="isActive"
                      checked={form.watch("isActive")}
                      onCheckedChange={(val) => form.setValue("isActive", val)}
                    />
                    <Label htmlFor="isActive">Active</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="isSkippable"
                      checked={form.watch("isSkippable")}
                      onCheckedChange={(val) =>
                        form.setValue("isSkippable", val)
                      }
                    />
                    <Label htmlFor="isSkippable">Skippable</Label>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/admin/onboarding")}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={flowMutation.isPending}>
                  {flowMutation.isPending
                    ? "Saving..."
                    : isEditMode
                    ? "Update Flow"
                    : "Create Flow"}
                </Button>
              </CardFooter>
            </form>
          </Card>

          {/* Pages card (only in edit mode) */}
          {isEditMode && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle>Pages</CardTitle>
                  <CardDescription>
                    Drag to reorder. Click a page to preview it.
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingPage(null);
                    setPageFormOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Page
                </Button>
              </CardHeader>
              <CardContent>
                {flow?.pages && flow.pages.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={flow.pages.map((p) => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {flow.pages.map((page) => (
                          <SortablePageCard
                            key={page.id}
                            page={page}
                            isSelected={selectedPageId === page.id}
                            onClick={() => setSelectedPageId(page.id)}
                            onEdit={() => {
                              setEditingPage(page);
                              setPageFormOpen(true);
                            }}
                            onDelete={() => setDeletingPageId(page.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No pages yet. Click "Add Page" to create one.
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Sticky preview */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Preview
                </CardTitle>
                <div className="flex gap-1 rounded-lg border p-1 dark:border-gray-700">
                  {LOCALES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      className={`px-2 py-0.5 text-xs font-medium rounded-md transition-colors ${
                        previewLocale === l.code
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={() => setPreviewLocale(l.code)}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="flex justify-center pb-6">
                <OnboardingPreview
                  page={selectedPage}
                  totalPages={flow?.pages?.length || 1}
                  currentIndex={selectedPage?.position || 0}
                  isSkippable={form.watch("isSkippable")}
                  locale={previewLocale}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Page form dialog */}
      {isEditMode && (
        <OnboardingPageForm
          open={pageFormOpen}
          onOpenChange={setPageFormOpen}
          flowId={Number(id)}
          page={editingPage}
          position={flow?.pages?.length || 0}
          totalPages={(flow?.pages?.length || 0) + (editingPage ? 0 : 1)}
          isSkippable={form.watch("isSkippable")}
        />
      )}

      {/* Delete page confirmation */}
      <AlertDialog
        open={!!deletingPageId}
        onOpenChange={() => setDeletingPageId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Page</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this page? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deletingPageId) deletePageMutation.mutate(deletingPageId);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
